import { useEffect, useMemo, useState } from 'react';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import AutoComplete from '@mui/material/Autocomplete';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Alert } from '@mui/material';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { DropdownItem } from 'types/api/SystemTenant/AROKMS/TableDefinitionTypes';
import { useGetCubeDefinitionDetails, useGetPullCubeDropdown } from 'services/v1/SystemTenant/AROCube/CubeBuilderService';
import { useGetCubeLayerSetLayersDropdown } from 'services/v1/SystemTenant/AROCube/CubeLayerSetService';
import { useGetTimePeriodSetDetail } from 'services/v1/SystemTenant/AROCube/CubeTimePeriodSetService';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import { CubeDefinitionRowItem, PullCubeDropdownItem } from 'types/api/SystemTenant/AROCube/CubeBuilderTypes';
import { CUBE_MAP_DATA_SPREAD_MODE, CUBE_TIME_PERIOD_SIZE_ORDER } from 'constant/CubeConstant';
import { ModalCubeDefinitionParamsProps } from './ModalCubeDefinitionParameter';
import { FormCubeDefinitionMetaDataValues } from './CubeDefinitionSettingPanel';
import { useParams } from 'react-router';

const autoCompleteStyle = {
  width: 240,
  '& .MuiAutocomplete-popper': {
    backgroundColor: 'red !imoprtant',
    fontSize: '10px',
  },
};

const ModalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 560,
  bgcolor: 'background.paper',
  maxHeight: 600,
  overflow: 'auto',
  borderRadius: 1,
  py: 2,
  // This is a flex column; without this, when content exceeds maxHeight the children shrink (flex-shrink: 1)
  // and their content overflows their boxes, overlapping each other instead of letting the modal scroll.
  '& > *': { flexShrink: 0 },
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

export type FormikMapDataLayerMappingItem = {
  id?: string;
  targetPullCube: AutoCompleteItem | null;
  sourceCubeLayer: AutoCompleteItem | null;
  destinationCubeLayer: AutoCompleteItem | null;
};

export type MapDataFromCubeToCubeParameterPayload = {
  rowItem: CubeDefinitionRowItem;
  mapDataLayerMappings: FormikMapDataLayerMappingItem[];
};

const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: AutoCompleteItem) => (
  <Box component='li' sx={{ '& > span': { fontSize: '14px', mr: 1, flexShrink: 0 } }} {...props}>
    <span>{option.label}</span>
  </Box>
);

const SPREAD_POLICY_OPTIONS = [
  {
    value: CUBE_MAP_DATA_SPREAD_MODE.DUPLICATE,
    label: 'Duplicate Spread',
    description:
      'Copy the full source value into every destination period it covers. A yearly value of 1,200 shows as 1,200 in each of the 12 months.',
  },
  {
    value: CUBE_MAP_DATA_SPREAD_MODE.PROPORTIONAL,
    label: 'Proportional Allocation',
    description:
      'Divide the source value by the number of destination periods it covers, so the parts add back up to the original. A yearly value of 1,200 becomes 100 in each of the 12 months.',
  },
];

// Larger period => higher rank (YEAR is largest). Returns -1 for unknown/missing types.
const periodSizeRank = (type?: string) => {
  const index = CUBE_TIME_PERIOD_SIZE_ORDER.indexOf(type ?? '');
  return index === -1 ? -1 : CUBE_TIME_PERIOD_SIZE_ORDER.length - index;
};

// ruleTypeParams stores the rule's config as a JSON string, e.g. {"spreadMode":"DUPLICATE"}. Older data
// (or no config) may not be valid JSON, so fall back to null.
const parseSpreadMode = (rawRuleTypeParams?: string): string | null => {
  if (!rawRuleTypeParams) return null;
  try {
    const parsed = JSON.parse(rawRuleTypeParams);
    return typeof parsed?.spreadMode === 'string' ? parsed.spreadMode : null;
  } catch {
    return null;
  }
};

export function ModalMapDataFromCubeToCube(
  props: ModalCubeDefinitionParamsProps<MapDataFromCubeToCubeParameterPayload> & {
    cubeMetaData: FormCubeDefinitionMetaDataValues;
    cubeId?: string;
  }
) {
  const { onClose, visible = false, onSubmitParameter, cubeDefinitionItem, cubeMetaData, cubeId } = props;
  const { cubeId: cubeIdParam } = useParams<{ cubeId: string }>();
  const currentCubeId = cubeId ?? cubeIdParam;

  const [selectedCubeId, setSelectedCubeId] = useState<string | null>(null);
  const [layerMappings, setLayerMappings] = useState<FormikMapDataLayerMappingItem[]>([]);
  const [spreadMode, setSpreadMode] = useState<string>(CUBE_MAP_DATA_SPREAD_MODE.DUPLICATE);

  const isCubeMetaDataReady = Boolean(cubeMetaData.cubeName && cubeMetaData.timePeriod && cubeMetaData.layerSet);

  const { data: cubeDefinitionDetails } = useGetCubeDefinitionDetails(currentCubeId);

  // Destination cube's period granularity (MONTH/YEAR/...). Resolved from the time-period-set detail keyed
  // by the (reliable) time-period-set id, because cubeMetaData.timePeriod.type is not populated when editing
  // — there the value comes from the cube details, which omits the type. Falls back to it when present.
  const { data: destinationTimePeriodSet } = useGetTimePeriodSetDetail(cubeMetaData.timePeriod?.value?.toString());
  const destinationPeriodType = destinationTimePeriodSet?.data.timePeriod || cubeMetaData.timePeriod?.type;

  // Unlike "Pull From Cube", this rule does not constrain candidates by layer set or time-period type, so
  // both are intentionally omitted from the request (qs.stringify drops the undefined params). Candidates are
  // scoped by attribute only; the layer-set difference is resolved per selected cube from the response.
  const { data: pullCubeDropdownOptions, isLoading: isLoadingPullCubeOptions } = useGetPullCubeDropdown({
    attributeId: cubeDefinitionItem?.attribute.value,
  });

  const { data: destinationCubeLayers, isLoading: isLoadingDestinationLayers } = useGetCubeLayerSetLayersDropdown(
    cubeMetaData.layerSet?.value
  );

  const pullCubeDropdownOptionsFiltered = useMemo(() => {
    if (!pullCubeDropdownOptions) return [];
    return pullCubeDropdownOptions.data.filter((option) => option.value !== currentCubeId);
  }, [pullCubeDropdownOptions, currentCubeId]);

  // Resolve the selected source cube against the (enriched) dropdown response. Each option carries the
  // cube's layer set id and its layers, so layer-set comparison and source-layer options need no extra calls.
  const selectedCube = useMemo(
    () => pullCubeDropdownOptionsFiltered.find((option) => option.value === selectedCubeId) ?? null,
    [pullCubeDropdownOptionsFiltered, selectedCubeId]
  );

  const sourceLayerOptions = useMemo<AutoCompleteItem[]>(
    () => (selectedCube?.layers ?? []).map((layer) => ({ value: layer.layerId, label: layer.layerName })),
    [selectedCube]
  );

  const savedLayerMappings = useMemo(() => {
    const attributeId = cubeDefinitionItem?.attribute.value;
    if (!attributeId) return undefined;
    return cubeDefinitionDetails?.data?.attributesMapDataLayerMappings?.[attributeId];
  }, [cubeDefinitionDetails?.data, cubeDefinitionItem?.attribute.value]);

  // Initialise the selected source cube and spread mode from the row item (when editing a configured rule).
  useEffect(() => {
    setSelectedCubeId(cubeDefinitionItem?.targetPullCube?.value ?? null);
    setSpreadMode(parseSpreadMode(cubeDefinitionItem?.ruleTypeParams) ?? CUBE_MAP_DATA_SPREAD_MODE.DUPLICATE);
  }, [cubeDefinitionItem]);

  // Content signature of the data that seeds the mapping rows. Gating the rebuild on this (rather than
  // on the array references) keeps a user's in-progress source-layer picks from being wiped if react-query
  // refetches the dropdowns with identical content while the modal is open.
  const layerMappingSeedSignature = useMemo(() => {
    const destinationPart = (destinationCubeLayers?.data ?? []).map((layer) => layer.value).join(',');
    const savedPart = (savedLayerMappings ?? [])
      .map(
        (mapping) =>
          `${mapping.destinationCubeLayer?.value ?? ''}:${mapping.sourceCubeLayer?.value ?? ''}:${
            mapping.targetPullCube?.value ?? ''
          }:${mapping.id ?? ''}`
      )
      .join('|');
    return `${destinationPart}__${savedPart}`;
  }, [destinationCubeLayers?.data, savedLayerMappings]);

  // Build one mapping row per destination layer, pre-filling with any previously saved source layer.
  useEffect(() => {
    const destinationLayers = destinationCubeLayers?.data ?? [];
    const savedByDestinationLayerId = new Map<string, FormikMapDataLayerMappingItem>();
    savedLayerMappings?.forEach((mapping) => {
      if (mapping.destinationCubeLayer?.value != null) {
        savedByDestinationLayerId.set(mapping.destinationCubeLayer.value.toString(), mapping);
      }
    });

    setLayerMappings(
      destinationLayers.map((destinationCubeLayer) => {
        const saved = savedByDestinationLayerId.get(destinationCubeLayer.value.toString());
        return {
          id: saved?.id,
          targetPullCube: saved?.targetPullCube ?? null,
          sourceCubeLayer: saved?.sourceCubeLayer ?? null,
          destinationCubeLayer,
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerMappingSeedSignature]);

  const handleOnCloseModal = () => {
    onClose?.();
    setSelectedCubeId(null);
    setLayerMappings([]);
  };

  const handleOnSelectCube = (value: PullCubeDropdownItem | null) => {
    setSelectedCubeId(value?.value ?? null);
    // Source layers belong to the selected cube, so clear them whenever the cube changes.
    setLayerMappings((prevData) => prevData.map((item) => ({ ...item, sourceCubeLayer: null })));
  };

  const handleOnSelectSourceLayer = (index: number, value: AutoCompleteItem | null) => {
    setLayerMappings((prevData) => {
      const newData = [...prevData];
      newData[index] = { ...newData[index], sourceCubeLayer: value };
      return newData;
    });
  };

  // True while we have a selected cube id (e.g. when editing) but the dropdown response that carries its
  // layer set / layers has not loaded yet.
  const isResolvingSourceCube = Boolean(selectedCubeId) && !selectedCube && isLoadingPullCubeOptions;

  // The layer-set mapping is only shown when the source cube uses a different layer set than this cube.
  // When both share the same layer set, layers line up directly and no manual mapping is shown. Compared
  // case-insensitively so a casing difference in the ids can't make matching layer sets look different.
  const isLayerSetDifferent = useMemo(() => {
    if (!selectedCube) return false;
    const sourceLayerSetId = selectedCube.layerSetId?.toString().trim().toUpperCase();
    const destinationLayerSetId = cubeMetaData.layerSet?.value?.toString().trim().toUpperCase();
    if (!sourceLayerSetId || !destinationLayerSetId) return false;
    return sourceLayerSetId !== destinationLayerSetId;
  }, [selectedCube, cubeMetaData.layerSet?.value]);

  // The spread policy is only relevant when the source period is larger (coarser) than the destination
  // period, e.g. pulling YEAR data into a MONTH cube — one source value must be spread across many periods.
  const isSourcePeriodLarger = useMemo(() => {
    if (!selectedCube) return false;
    const sourceRank = periodSizeRank(selectedCube.timePeriodType);
    const destinationRank = periodSizeRank(destinationPeriodType);
    if (sourceRank < 0 || destinationRank < 0) return false;
    return sourceRank > destinationRank;
  }, [selectedCube, destinationPeriodType]);

  // A rule already saved with a spread mode must always expose the policy so it can be changed — even if the
  // live period comparison can't be evaluated yet (period metadata still loading, etc.).
  const hasSavedSpreadMode = useMemo(
    () => parseSpreadMode(cubeDefinitionItem?.ruleTypeParams) != null,
    [cubeDefinitionItem]
  );
  const isSpreadApplicable = Boolean(selectedCube) && (isSourcePeriodLarger || hasSavedSpreadMode);

  const isFormValid = useMemo(() => {
    if (!selectedCube || isResolvingSourceCube) return false;
    if (isLayerSetDifferent && !layerMappings.some((item) => Boolean(item.sourceCubeLayer))) return false;
    if (isSpreadApplicable && !spreadMode) return false;
    return true;
  }, [selectedCube, isResolvingSourceCube, isLayerSetDifferent, layerMappings, isSpreadApplicable, spreadMode]);

  const handleOnSave = () => {
    if (!cubeDefinitionItem || !selectedCube) return;

    const targetPullCube: DropdownItem = { value: selectedCube.value, label: selectedCube.label };

    // Layer mappings only apply when the layer sets differ; when they match the layers line up 1:1 and no
    // mapping is shown or sent.
    const mapDataLayerMappings = isLayerSetDifferent
      ? layerMappings.filter((item) => Boolean(item.sourceCubeLayer)).map((item) => ({ ...item, targetPullCube }))
      : [];

    // ruleTypeParams carries the rule config as JSON; spreadMode is included only when spreading applies.
    const ruleParams: { spreadMode?: string } = {};
    if (isSpreadApplicable && spreadMode) {
      ruleParams.spreadMode = spreadMode;
    }

    const rowItem: CubeDefinitionRowItem = {
      ...cubeDefinitionItem,
      targetPullCube,
      targetPullCubeAttribute: cubeDefinitionItem.attribute,
      targetPullCubeLayer: undefined,
      ruleTypeParams: JSON.stringify(ruleParams),
    };

    onSubmitParameter?.({ rowItem, mapDataLayerMappings });
    handleOnCloseModal();
  };

  return (
    <div>
      <Modal open={visible} onClose={handleOnCloseModal}>
        <Stack direction='column' sx={ModalContentStyle}>
          <Stack px={2} justifyContent='space-between' direction='row' alignItems='center'>
            <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
              Map Data From Cube to Cube
            </Typography>
            <IconButton onClick={handleOnCloseModal} component='label'>
              <HighlightOffRoundedIcon sx={closeIconStyle} />
            </IconButton>
          </Stack>
          <Divider sx={{ mb: 1 }} />

          <Stack
            px={2}
            sx={{
              bgcolor: '#FBFBFB',
              py: 2,
              borderTop: '1px solid #E3EBF6',
              borderBottom: '1px solid #E3EBF6',
            }}
          >
            <Typography variant='input-label' fontWeight='bold' color='black' mb={1}>
              Select Source Cube :
            </Typography>
            <Typography variant='input-label-gray' mb={2}>
              Select the cube you would like to map the data from into {cubeDefinitionItem?.attribute.label}.
            </Typography>
            {!isCubeMetaDataReady && (
              <Stack mb={1}>
                <Alert severity='warning'>
                  <Typography variant='body2'>
                    Please define the cube name, time period, and layer set for the cube before mapping the data.
                  </Typography>
                </Alert>
              </Stack>
            )}
            <AutoComplete
              // @ts-ignore
              onChange={(event, value) => handleOnSelectCube(value as PullCubeDropdownItem)}
              clearIcon={null}
              size='small'
              value={selectedCube}
              disabled={isLoadingPullCubeOptions || !isCubeMetaDataReady}
              getOptionLabel={(option: AutoCompleteItem) => option.label}
              isOptionEqualToValue={(option: AutoCompleteItem, value: AutoCompleteItem) => option.value === value.value}
              options={pullCubeDropdownOptionsFiltered}
              renderOption={renderOption}
              sx={autoCompleteStyle}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name='targetPullCube'
                  placeholder={isLoadingPullCubeOptions ? 'Loading...' : 'Please Select Cube'}
                />
              )}
            />
          </Stack>

          <Stack px={2} pt={2} minHeight={120}>
            {!selectedCube && !isResolvingSourceCube && (
              <Alert severity='info'>
                <Typography variant='body2'>Select a source cube to continue.</Typography>
              </Alert>
            )}

            {isResolvingSourceCube && (
              <Stack height={200}>
                <ActivityIndicator />
              </Stack>
            )}

            {selectedCube && !isLayerSetDifferent && (
              <Alert severity='info'>
                <Typography variant='body2'>
                  The source cube shares this cube&apos;s layer set, so the layers line up directly. No layer mapping is
                  needed.
                </Typography>
              </Alert>
            )}

            {selectedCube && isLayerSetDifferent && (
              <>
                <Typography variant='input-label' fontWeight='bold' color='black' mb={1}>
                  Map Layer Set :
                </Typography>
                <Typography variant='input-label-gray' mb={2}>
                  The source cube uses a different layer set. Map each layer of this cube to the layer in the source cube
                  that the data should be pulled from.
                </Typography>

                {isLoadingDestinationLayers && !destinationCubeLayers ? (
                  <Stack height={160}>
                    <ActivityIndicator />
                  </Stack>
                ) : layerMappings.length === 0 ? (
                  <Alert severity='warning'>
                    <Typography variant='body2'>This cube has no layers to map.</Typography>
                  </Alert>
                ) : (
                  layerMappings.map((item, index) => (
                    <Stack key={item.destinationCubeLayer?.value ?? index}>
                      <Stack direction='row' alignItems='center' justifyContent='space-between' gap={1} py={0.5}>
                        <Stack flex={1}>
                          <Typography variant='input-label-gray' mb={0.5}>
                            Destination Layer
                          </Typography>
                          <Typography variant='body2' fontWeight='bold' color='black'>
                            {item.destinationCubeLayer?.label}
                          </Typography>
                        </Stack>
                        <ArrowForwardRoundedIcon sx={{ color: '#98A2AE', mt: 2.5 }} fontSize='small' />
                        <Stack flex={1}>
                          <Typography variant='input-label-gray' mb={0.5}>
                            Source Layer
                          </Typography>
                          <AutoComplete
                            // @ts-ignore
                            onChange={(event, value) => handleOnSelectSourceLayer(index, value as AutoCompleteItem)}
                            clearIcon={null}
                            size='small'
                            value={item.sourceCubeLayer}
                            getOptionLabel={(option: AutoCompleteItem) => option.label}
                            isOptionEqualToValue={(option: AutoCompleteItem, value: AutoCompleteItem) =>
                              option.value === value.value
                            }
                            options={sourceLayerOptions}
                            renderOption={renderOption}
                            sx={{ width: 240 }}
                            renderInput={(params) => (
                              <TextField {...params} name='sourceCubeLayer' placeholder='Please Select Layer' />
                            )}
                          />
                        </Stack>
                      </Stack>
                      {index < layerMappings.length - 1 && <Divider sx={{ my: 0.5 }} />}
                    </Stack>
                  ))
                )}
              </>
            )}
          </Stack>

          {isSpreadApplicable && (
            <Stack px={2} mt={3}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant='input-label' fontWeight='bold' color='black' mb={1}>
                Spread Policy :
              </Typography>
              <Typography variant='input-label-gray' mb={1.5}>
                {isSourcePeriodLarger && destinationPeriodType
                  ? `The source cube uses a larger time period (${selectedCube?.timePeriodType}) than this cube (${destinationPeriodType}). `
                  : ''}
                Choose how each source value should be spread across the destination periods it covers.
              </Typography>
              <RadioGroup value={spreadMode} onChange={(event) => setSpreadMode(event.target.value)}>
                {SPREAD_POLICY_OPTIONS.map((option) => (
                  <Stack
                    key={option.value}
                    sx={{
                      border: '1px solid #E3EBF6',
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                      mb: 1,
                      bgcolor: spreadMode === option.value ? '#F5F7FF' : '#FFFFFF',
                    }}
                  >
                    <FormControlLabel
                      value={option.value}
                      control={<Radio size='small' />}
                      label={
                        <Typography variant='body2' fontWeight='bold' color='black'>
                          {option.label}
                        </Typography>
                      }
                      sx={{ m: 0, gap: 0.5 }}
                    />
                    <Typography variant='input-label-gray' ml={3.75}>
                      {option.description}
                    </Typography>
                  </Stack>
                ))}
              </RadioGroup>
            </Stack>
          )}

          <Stack px={2}>
            <Divider sx={{ mb: 1, mt: 2 }} />
            <Stack direction='row' justifyContent='flex-end' spacing={2} alignItems='center' sx={{ py: 1 }}>
              <Button variant='main-table-panel-border' onClick={handleOnCloseModal}>
                Cancel
              </Button>
              <ButtonLoading variant='main-table-panel' disabled={!isFormValid} onClick={() => handleOnSave()}>
                Save Parameter
              </ButtonLoading>
            </Stack>
          </Stack>
        </Stack>
      </Modal>
    </div>
  );
}
